import {
  ArrowUpIcon,
  CameraIcon,
  ChevronLeftIcon,
  InformationCircleIcon,
  MicrophoneIcon,
  PaperClipIcon,
  VideoCameraIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import { getSocket } from "../utils/useChatSocket";
import { RiSpeakLine } from "react-icons/ri";
import html2canvas from "html2canvas";
import DOMPurify from "dompurify";
import axios from "axios";
import dayjs from "dayjs";
import "../CSS/Message.css";
import Feedback from "./Feedback";
import FeedbackForm from "./FeedbackForm";
import RenderAttachment from "./attachmentRenders/RenderAttachment";
import { getAgentInfoById, getCachedAgentInfo } from "../utils/agentService";

const socket = getSocket();

const Message = ({
  setMessage,
  message,
  setAttachment,
  setAttachmentType,
  sendMessage,
  isOpen,
  setIsOpen,
  goBack,
  selectedConversation,
  setSelectedConversation,
  setConversations,
  setupDetails,
  setView,
  widgetConfigs,
}) => {
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recognitionRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [showDetails, setShowDetails] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerIntervalRef = useRef(null);
  const [isScreenRecording, setIsScreenRecording] = useState(false);

  const primaryColor =
    setupDetails?.widgetCustomizations?.primaryColor ||
    setupDetails?.widgetCustomizations?.color1 ||
    "#000000";

  useEffect(() => {
    const scrollToBottom = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    };

    scrollToBottom();
  }, [selectedConversation?.messages]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachmentType(file.type);
      sendMessage(file);
      e.target.value = null;
    }
  };

  const removeQuotedText = (msg) => {
    if (!msg) return "";
    const quotedTextPattern =
      /(?:^On .+ wrote:|^From: .+|^\s*>.*(?:\n|$)|^>.*(?:\n|$))/gms;
    return msg.replace(quotedTextPattern, "").trim();
  };

  const handleIconClick = () => {
    fileInputRef.current?.click();
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const now = new Date();
    const past = new Date(dateStr);
    const diffInSec = Math.floor((now - past) / 1000);
    if (diffInSec < 60) return "Just now";
    const diffInMin = Math.floor(diffInSec / 60);
    if (diffInMin < 60) return `${diffInMin}m ago`;
    const diffInHours = Math.floor(diffInMin / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
    return dayjs(dateStr).format("MMM D");
  };

  const getInitials = (name) => {
    if (!name) return "A";
    const clean = name.replace(/@.*$/, "").trim();
    const parts = clean.split(/[.\s_-]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts.length === 1 && parts[0].length >= 1) {
      return parts[0][0].toUpperCase();
    }
    return (clean[0] || "A").toUpperCase();
  };

  const formatSenderName = (name) => {
    if (!name) return "Support Agent";
    const clean = name.replace(/@.*$/, "").trim();
    const parts = clean.split(/[.\s_-]+/).filter(Boolean);
    if (parts.length > 0) {
      return parts
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
        .join(" ");
    }
    return clean;
  };

  const [, setProfileUpdateTick] = useState(0);

  // Fetch agent profile info from auth microservice for messages
  useEffect(() => {
    if (selectedConversation?.messages && Array.isArray(selectedConversation.messages)) {
      selectedConversation.messages.forEach((m) => {
        if (m.sender !== "Customer" && m.senderId && !getCachedAgentInfo(m.senderId)) {
          getAgentInfoById(m.senderId).then(() => {
            setProfileUpdateTick((prev) => prev + 1);
          });
        }
      });
    }
  }, [selectedConversation?.messages]);

  const resolveAgentDetails = (msg, setupDetails, selectedConversation) => {
    const cached = msg?.senderId ? getCachedAgentInfo(msg.senderId) : null;
    const rawName =
      cached?.name ||
      msg.user?.name ||
      msg.agentName ||
      msg.senderName ||
      (msg.fromAddress ? msg.fromAddress.split("@")[0] : "") ||
      selectedConversation?.agent?.name ||
      selectedConversation?.assignedTo?.name ||
      setupDetails?.agent?.name ||
      setupDetails?.widgetCustomizations?.agentName ||
      "Support Agent";

    const rawAvatar =
      cached?.avatar ||
      msg.avatar ||
      msg.user?.avatar ||
      msg.user?.image ||
      msg.user?.profilePic ||
      msg.user?.profileImage ||
      msg.agent?.avatar ||
      msg.agentAvatar ||
      selectedConversation?.agent?.avatar ||
      selectedConversation?.assignedTo?.avatar ||
      setupDetails?.agent?.avatar ||
      setupDetails?.widgetCustomizations?.botAvatar ||
      setupDetails?.widgetCustomizations?.avatar ||
      null;

    return {
      name: formatSenderName(rawName),
      avatar: rawAvatar,
      initials: cached?.initials || getInitials(rawName),
    };
  };

  const captureScreenshot = async () => {
    const host = document.getElementById("chat-widget-container");
    const shadowRoot = host?.shadowRoot;
    const chatPanel = shadowRoot?.querySelector(".dexkor-chat-widget-panel");
    const draggableTrigger = shadowRoot?.querySelector(".dexkor-draggable-trigger");

    try {
      if (chatPanel) chatPanel.style.display = "none";
      if (draggableTrigger) draggableTrigger.style.display = "none";

      await new Promise((resolve) => requestAnimationFrame(resolve));

      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft;

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: window.devicePixelRatio || 2,
        logging: false,
        x: scrollX,
        y: scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
      });

      canvas.toBlob(async (blob) => {
        if (chatPanel) chatPanel.style.display = "block";
        if (draggableTrigger) draggableTrigger.style.display = "block";

        if (blob) {
          const file = new File([blob], `screenshot-${Date.now()}.png`, { type: "image/png" });
          await sendMessage(file);
        }
      }, "image/png", 1.0);

    } catch (error) {
      if (chatPanel) chatPanel.style.display = "block";
      if (draggableTrigger) draggableTrigger.style.display = "block";
      console.error("Screenshot failed:", error);
    }
  };

  const startScreenRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: "video/webm; codecs=vp9",
      });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: "video/webm" });
        const file = new File([blob], `screen-record-${Date.now()}.webm`, {
          type: "video/webm",
        });

        await sendMessage(file);

        audioChunksRef.current = [];
        setIsScreenRecording(false);
        clearInterval(timerIntervalRef.current);
        setRecordingTime(0);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();

      setIsScreenRecording(true);
      setRecordingTime(0);
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      setTimeout(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
        }
      }, 8000);
    } catch (error) {
      console.error("Error starting screen recording:", error);
      setIsScreenRecording(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const file = new File([blob], "voicemail.webm", { type: "audio/webm" });
        await sendMessage(file);
        audioChunksRef.current = [];
        setRecording(false);
        clearInterval(timerIntervalRef.current);
        setRecordingTime(0);
      };

      mediaRecorderRef.current.start();
      setRecording(true);
      setRecordingTime(0);
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting audio recording:", error);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
      clearInterval(timerIntervalRef.current);
    }
  };

  let touchTimer;

  const startTranscription = () => {
    if (!("webkitSpeechRecognition" in window)) {
      console.error("Speech recognition is not supported in this browser.");
      return;
    }

    recognitionRef.current = new window.webkitSpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = "en-US";

    let finalTranscript = "";

    recognitionRef.current.onresult = (event) => {
      let interimTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }
      setMessage(finalTranscript + interimTranscript);
    };

    recognitionRef.current.start();
    setTranscribing(true);
  };

  const stopTranscription = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setTranscribing(false);
    }
  };

  const handleTouchStart = () => {
    clearTimeout(touchTimer);
    touchTimer = setTimeout(() => {
      startTranscription();
    }, 100);
  };

  const handleTouchEnd = () => {
    clearTimeout(touchTimer);
    stopTranscription();
  };

  const handleTouchCancel = () => {
    stopTranscription();
  };

  const [customerDetails, setCustomerDetails] = useState(null);
  useEffect(() => {
    setCustomerDetails(
      JSON.parse(localStorage.getItem("dexkor-chat-widget-customer-details"))
    );
  }, []);

  const [ticketFormFields, setTicketFormFields] = useState(null);
  const [isFormFieldsLoading, setIsFormFieldsLoading] = useState(false);
  useEffect(() => {
    setIsFormFieldsLoading(true);
    const getTicketForm = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/widget/ticket-form/${widgetConfigs.uniquePartnerId}`
        );
        const formFields = response?.data?.data?.fields;
        const fieldsToSendInInitalFields = formFields?.filter(
          (field) =>
            field.fieldType === "text" ||
            field.fieldType === "textarea" ||
            field.fieldType === "number" ||
            field.fieldType === "email" ||
            field.fieldType === "date"
        );
        setTicketFormFields(fieldsToSendInInitalFields);
        setIsFormFieldsLoading(false);
      } catch (error) {
        console.error("Error fetching form config:", error);
        setIsFormFieldsLoading(false);
      }
    };
    if (widgetConfigs?.uniquePartnerId) {
      getTicketForm();
    }
  }, [widgetConfigs?.uniquePartnerId]);

  const feedbackRef = useRef(null);

  useEffect(() => {
    const s = selectedConversation?.ticketId?.status?.toLowerCase();
    if (s === "resolved" || s === "closed") {
      setTimeout(() => {
        feedbackRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [selectedConversation?.ticketId?.status]);

  const formatStatus = (status) => {
    if (!status) return "Open";
    const statusMap = {
      unassigned: "Unassigned",
      assigned: "Assigned",
      in_progress: "In Progress",
      awaiting_customer: "Awaiting Customer",
      awaiting_third_party: "Awaiting Third Party",
      snoozed: "Snoozed",
      resolved: "Resolved",
      duplicate: "Duplicate",
      merged: "Merged",
      cancelled: "Cancelled",
      reopen: "Reopen",
      closed: "Closed",
    };
    return (
      statusMap[status.toLowerCase()] ||
      status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  return (
    <>
      {isOpen && (
        <div className="flex flex-col h-full bg-white overflow-hidden font-sans">
          {/* Gleap-Style Header */}
          <div className="flex items-center justify-between px-3.5 py-3 border-b border-slate-100 bg-white shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              {/* Back Button */}
              <button
                onClick={goBack}
                className="w-8 h-8 rounded-full flex items-center justify-center text-slate-700 hover:text-slate-900 hover:bg-slate-100 transition-colors border-none bg-transparent cursor-pointer p-0 shrink-0"
                title="Back to tickets"
              >
                <ChevronLeftIcon className="w-5 h-5 stroke-[2.2]" />
              </button>

              {/* Overlapping Team Avatars */}
              {/* <div className="flex items-center -space-x-2 shrink-0">
                <img
                  src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=64&h=64&fit=crop&crop=face"
                  alt="Team member 1"
                  className="w-7 h-7 rounded-full ring-2 ring-white object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=face"
                  alt="Team member 2"
                  className="w-7 h-7 rounded-full ring-2 ring-white object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=64&h=64&fit=crop&crop=face"
                  alt="Team member 3"
                  className="w-7 h-7 rounded-full ring-2 ring-white object-cover"
                />
              </div> */}

              {/* Partner Name and Subtitle */}
              <div className="flex flex-col min-w-0 ml-1">
                <span className="text-sm font-bold text-slate-900 leading-tight truncate">
                    {selectedConversation?.ticketId?.subject || "No Subject"}
                </span>
                <span className="text-[11px] text-slate-400 font-normal leading-tight mt-0.5 truncate">
                  {/* We usually reply within a few hours.  */}
                  {selectedConversation?.ticketId?.description || "No Description Provided"}

                </span>
              </div>
            </div>

            {/* Header Right Actions */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setShowDetails(!showDetails)}
                className={`p-1.5 rounded-full transition-colors border-none cursor-pointer ${
                  showDetails
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-400 hover:text-slate-700 hover:bg-slate-50 bg-transparent"
                }`}
                title={showDetails ? "Back to conversation" : "View ticket details"}
              >
                <InformationCircleIcon className="w-5 h-5 stroke-[1.8]" />
              </button>

              <button
                onClick={() => setIsOpen && setIsOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors border-none bg-transparent cursor-pointer p-0"
                title="Close chat"
              >
                <XMarkIcon className="w-5 h-5 stroke-[2]" />
              </button>
            </div>
          </div>

          {/* VIEW 1: TICKET DETAILS VIEW */}
          {showDetails ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-4 dexkor-scrollbar bg-slate-50/50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Ticket Information
                </span>
                <button
                  type="button"
                  onClick={() => setShowDetails(false)}
                  className="text-xs font-semibold text-blue-600 hover:underline border-none bg-transparent cursor-pointer"
                >
                  Return to chat
                </button>
              </div>

              {/* Status and Title Card */}
              <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-800 font-mono">
                    #{selectedConversation?.ticketId?.ticketNumber || "—"}
                  </span>
                  <div className="flex items-center gap-2">
                    {selectedConversation?.ticketId?.priority && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full capitalize bg-blue-50 text-blue-700 border border-blue-100">
                        {selectedConversation.ticketId.priority}
                      </span>
                    )}
                    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                      {formatStatus(selectedConversation?.ticketId?.status || "open")}
                    </span>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-bold text-slate-900 leading-snug">
                    {selectedConversation?.ticketId?.subject || "No Subject"}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Created on{" "}
                    {selectedConversation?.ticketId?.createdAt
                      ? dayjs(selectedConversation.ticketId.createdAt).format("MMM DD, YYYY · hh:mm A")
                      : "—"}
                  </p>
                </div>
              </div>

              {/* Description Card */}
              <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs space-y-2">
                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Description
                </h4>
                <p className="text-xs sm:text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {selectedConversation?.ticketId?.description || "No description provided."}
                </p>
              </div>

              {/* Requester Info Card */}
              <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs space-y-2.5">
                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Requester Details
                </h4>
                <div className="divide-y divide-slate-100 text-xs text-slate-600">
                  <div className="py-2 flex items-center justify-between">
                    <span className="text-slate-400">Email</span>
                    <span className="font-semibold text-slate-800">{selectedConversation?.ticketId?.email || "—"}</span>
                  </div>
                  {selectedConversation?.ticketId?.source && (
                    <div className="py-2 flex items-center justify-between">
                      <span className="text-slate-400">Channel</span>
                      <span className="font-semibold text-slate-800 capitalize">{selectedConversation.ticketId.source}</span>
                    </div>
                  )}
                  {selectedConversation?.ticketId?.lastSender && (
                    <div className="py-2 flex items-center justify-between">
                      <span className="text-slate-400">Last Message From</span>
                      <span className="font-semibold text-slate-800">{selectedConversation.ticketId.lastSender}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* SLA Performance Card */}
              {(selectedConversation?.ticketId?.slaFirstResponseDue || selectedConversation?.ticketId?.slaResolutionDue) && (
                <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs space-y-2.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                      SLA Performance
                    </h4>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        selectedConversation?.ticketId?.slaBreached
                          ? "bg-rose-50 text-rose-700 border border-rose-200"
                          : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      }`}
                    >
                      {selectedConversation?.ticketId?.slaBreached ? "SLA Breached" : "On Track"}
                    </span>
                  </div>
                  <div className="divide-y divide-slate-100 text-xs text-slate-600">
                    {selectedConversation?.ticketId?.slaFirstResponseDue && (
                      <div className="py-2 flex items-center justify-between">
                        <span className="text-slate-400">First Response Due</span>
                        <span className="font-medium text-slate-800">
                          {dayjs(selectedConversation.ticketId.slaFirstResponseDue).format("MMM DD, hh:mm A")}
                        </span>
                      </div>
                    )}
                    {selectedConversation?.ticketId?.slaResolutionDue && (
                      <div className="py-2 flex items-center justify-between">
                        <span className="text-slate-400">Resolution Due</span>
                        <span className="font-medium text-slate-800">
                          {dayjs(selectedConversation.ticketId.slaResolutionDue).format("MMM DD, hh:mm A")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* VIEW 2: GLEAP-STYLE DISCUSSION VIEW */
            <>
              <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-4 dexkor-scrollbar-hidden-chat bg-white"
              >
                {/* 1. Initial Ticket Request (Subject + Description) rendered as customer bubble on the right */}
                {selectedConversation?.ticketId?.subject && (
                  <div className="flex flex-col items-end mb-4">
                    <div className="bg-black text-white rounded-2xl rounded-tr-xs px-4 py-3 max-w-[85%] shadow-xs">
                      <div className="font-bold text-xs sm:text-[13px] text-white leading-snug">
                        {selectedConversation.ticketId.subject}
                      </div>
                      {selectedConversation.ticketId.description && (
                        <div className="text-xs text-white/90 mt-1 whitespace-pre-wrap leading-relaxed">
                          {selectedConversation.ticketId.description}
                        </div>
                      )}
                    </div>
                    {selectedConversation.ticketId.createdAt && (
                      <div className="text-[11px] text-slate-400 mt-1 mr-1 text-right">
                        {formatTimeAgo(selectedConversation.ticketId.createdAt)}
                      </div>
                    )}
                  </div>
                )}

                {/* 2. Conversation Messages */}
                {selectedConversation?.messages?.map((msg, index) => {
                  if (msg.type === "Internal") return null;

                  const isCustomer = msg.sender === "Customer";
                  const agentDetails = !isCustomer
                    ? resolveAgentDetails(msg, setupDetails, selectedConversation)
                    : null;

                  return (
                    <div
                      key={index}
                      className={`flex flex-col ${isCustomer ? "items-end" : "items-start"} mb-3`}
                    >
                      {/* Agent avatar & name header with image and initials fallback */}
                      {!isCustomer && (
                        <div className="flex items-center gap-2 mb-1.5 ml-1">
                          <div className="w-6 h-6 shrink-0 relative">
                            {agentDetails.avatar ? (
                              <img
                                src={agentDetails.avatar}
                                alt={agentDetails.name}
                                className="w-6 h-6 rounded-full object-cover border border-slate-200 shadow-2xs"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = "flex";
                                  }
                                }}
                              />
                            ) : null}
                            <div
                              style={{ display: agentDetails.avatar ? "none" : "flex" }}
                              className="w-6 h-6 rounded-full bg-slate-900 text-white items-center justify-center text-[10px] font-bold shadow-xs tracking-tight select-none"
                            >
                              {agentDetails.initials}
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-slate-800">
                            {agentDetails.name}
                          </span>
                        </div>
                      )}

                      {/* Attachments if present */}
                      {msg.attachments && (
                        <div className="mb-1.5">
                          <RenderAttachment
                            attachment={msg.attachment}
                            attachmentType={msg.attachmentType}
                            attachments={msg.attachments}
                          />
                        </div>
                      )}

                      {/* Message Bubble Row */}
                      {msg.message && (
                        <div className={`flex items-center ${isCustomer ? "justify-end" : "justify-start"} max-w-[92%]`}>
                          <div
                            className={`px-4 py-3 rounded-2xl text-xs sm:text-[13px] leading-relaxed shadow-xs ${
                              isCustomer
                                ? "bg-black text-white rounded-tr-xs"
                                : "bg-[#f1f3f5] text-slate-900 rounded-tl-xs"
                            }`}
                          >
                            <div
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(removeQuotedText(msg.message)),
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Relative Timestamp */}
                      <div className={`text-[11px] text-slate-400 mt-1 ${isCustomer ? "mr-1 text-right" : "ml-1"}`}>
                        {formatTimeAgo(msg.timestamp)}
                      </div>
                    </div>
                  );
                })}

                {/* Feedback Section with Auto-Scroll Anchor */}
                <div ref={feedbackRef} className="pt-2">
                  {["resolved", "Resolved"].includes(selectedConversation?.ticketId?.status) && (
                    <FeedbackForm
                      selectedConversation={selectedConversation}
                      setSelectedConversation={setSelectedConversation}
                      setConversations={setConversations}
                      setupDetails={setupDetails}
                    />
                  )}

                  {["closed", "Closed"].includes(selectedConversation?.ticketId?.status) && (
                    <Feedback
                      selectedConversation={selectedConversation}
                      setupDetails={setupDetails}
                    />
                  )}
                </div>
              </div>

              {/* Gleap-Style Bottom Input Area */}
              {!(["closed", "Closed", "resolved", "Resolved"].includes(selectedConversation?.ticketId?.status)) && (
                  <div className="p-3 bg-white border-t border-slate-100 shrink-0">
                    {/* Active Audio / Screen Recording Banner */}
                    {(recording || isScreenRecording || transcribing) && (
                      <div className="flex items-center justify-between px-3 py-1.5 mb-2 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                        <div className="flex items-center gap-2 font-medium">
                          <span className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
                          {recording && `Recording Audio: ${formatTime(recordingTime)}`}
                          {isScreenRecording && `Recording Screen: ${formatTime(recordingTime)}`}
                          {transcribing && "Listening to speech..."}
                        </div>
                        <button
                          type="button"
                          onClick={recording ? stopRecording : transcribing ? stopTranscription : () => {}}
                          className="text-[11px] font-bold text-red-600 hover:underline border-none bg-transparent cursor-pointer"
                        >
                          Stop
                        </button>
                      </div>
                    )}

                    {/* Rounded Input Box */}
                    <div className="border border-slate-300 hover:border-slate-400 focus-within:border-slate-800 rounded-3xl p-3 bg-white transition-all shadow-xs flex flex-col gap-1.5">
                      {/* Text Input */}
                      <textarea
                        placeholder="Write a reply..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (message?.trim()) sendMessage();
                          }
                        }}
                        rows={2}
                        className="w-full bg-transparent border-none text-xs sm:text-[13px] text-slate-800 placeholder-slate-400 outline-none resize-none px-1 py-0.5 focus:ring-0 leading-relaxed dexkor-scrollbar"
                      />

                      {/* Hidden File Input */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="hidden"
                      />

                      {/* Toolbar & Send Button */}
                      <div className="flex items-center justify-between pt-1">
                        {/* Media Action Icons */}
                        <div className="flex items-center gap-2.5 text-slate-400">
                          {/* Paperclip */}
                          <button
                            type="button"
                            onClick={handleIconClick}
                            className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors border-none bg-transparent cursor-pointer"
                            title="Attach file"
                          >
                            <PaperClipIcon className="w-5 h-5 stroke-[1.8]" />
                          </button>

                          {/* Microphone */}
                          <button
                            type="button"
                            onClick={recording ? stopRecording : startRecording}
                            className={`p-1 rounded-md transition-colors border-none cursor-pointer ${
                              recording
                                ? "text-red-500 bg-red-50 animate-pulse"
                                : "text-slate-400 hover:text-slate-700 hover:bg-slate-100 bg-transparent"
                            }`}
                            title="Record voice"
                          >
                            <MicrophoneIcon className="w-5 h-5 stroke-[1.8]" />
                          </button>

                          {/* Screenshot */}
                          <button
                            type="button"
                            onClick={captureScreenshot}
                            className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors border-none bg-transparent cursor-pointer"
                            title="Take screenshot"
                          >
                            <CameraIcon className="w-5 h-5 stroke-[1.8]" />
                          </button>

                          {/* Video / Screen Record */}
                          <button
                            type="button"
                            onClick={() => startScreenRecording()}
                            className={`p-1 rounded-md transition-colors border-none cursor-pointer ${
                              isScreenRecording
                                ? "text-red-500 bg-red-50 animate-pulse"
                                : "text-slate-400 hover:text-slate-700 hover:bg-slate-100 bg-transparent"
                            }`}
                            title="Record screen"
                          >
                            <VideoCameraIcon className="w-5 h-5 stroke-[1.8]" />
                          </button>

                          {/* Dictation */}
                          <button
                            type="button"
                            className={`p-1 rounded-md transition-colors border-none bg-transparent cursor-pointer ${
                              transcribing
                                ? "text-red-500 bg-red-50 animate-pulse"
                                : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                            }`}
                            onMouseDown={startTranscription}
                            onMouseUp={stopTranscription}
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                            onTouchCancel={handleTouchCancel}
                            title="Hold to dictate"
                          >
                            <RiSpeakLine className="w-5 h-5" />
                          </button>
                        </div>

                        {/* Send Button: Up Arrow in Circle */}
                        <button
                          type="button"
                          disabled={!message?.trim() && !recording}
                          onClick={recording ? stopRecording : sendMessage}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all border-none ${
                            message?.trim() || recording
                              ? "bg-black text-white hover:opacity-90 active:scale-95 cursor-pointer shadow-xs"
                              : "bg-slate-200 text-slate-400 cursor-not-allowed"
                          }`}
                          title="Send"
                        >
                          <ArrowUpIcon className="w-4 h-4 stroke-[2.5]" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Message;
